/**
 * Nav.jsx — sticky topbar voor de hele app.
 * Gemigreerd naar CSS Modules (Nav.module.css). Mobile state blijft in JS
 * voor open/dicht menu.
 */
import { useEffect, useState } from 'react';
import tofLogo from '../assets/tof-logo.png';
import styles from './Nav.module.css';
import { isAdminEmail } from '../supabase';
import { IS_NATIVE } from '../config/platform';
import { useCopy, useLang } from '../i18n/LanguageContext';
import { tap } from '../native/nativeShell';

export default function Nav({
    page,
    setPage,
    hasResult = false,
    currentUser = null,
    isManager = false,
    onLogout,
}) {
    const { nav: t } = useCopy();
    const { lang, setLang } = useLang();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 820);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 820);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!isMobile) setMenuOpen(false);
    }, [isMobile]);

    // Manager-mode: een ingelogde team-manager heeft geen behoefte aan de
    // marketing-flow (Home/Intro/Quiz). We tonen alleen "Mijn team(s)" en
    // — als hij óók admin is — "Admin". Niet-managers zien de volledige nav.
    // App-modus kent alleen stap 1: start, de vragenlijst, je profiel en de
    // bibliotheek. Geen teamomgeving, geen manager- of adminlinks.
    const baseItems = IS_NATIVE
        ? [
            { key: 'home', label: t.home },
            { key: 'intro', label: t.intro },
            { key: 'quiz', label: t.quiz },
        ]
        : isManager
            ? [
                { key: 'team', label: t.myTeam },
            ]
            : [
                // Web: de test zit in de app, dus geen "Eerst even uitleg"
                // en "Test jezelf" meer — /quiz toont hier het storescherm.
                { key: 'home', label: t.home },
                { key: 'quiz', label: t.app },
                { key: 'team', label: t.teamEnvironment },
            ];
    const resultItems = (hasResult && (IS_NATIVE || !isManager))
        ? [
            { key: 'results', label: t.results },
            // De gespreksvoorbereiding en de historie bestaan alleen in de
            // app: die staan allebei op het toestel bewaard.
            ...(IS_NATIVE
                ? [
                    { key: 'gesprek', label: t.conversation },
                    { key: 'historie', label: t.history },
                ]
                : []),
            { key: 'library', label: t.library },
        ]
        : [];
    // "Mijn teams" tijdelijk verborgen tot data-migratie + admin-dashboard af zijn.
    // Zonder user_id op responses kunnen we 'eigen teams' nog niet betrouwbaar tonen.
    // Zet terug op `currentUser ? [...] : []` wanneer dit klaar is.
    const managerItems = [];

    // Admin-link — alleen zichtbaar voor TOF-admins (zie ADMIN_EMAILS in supabase.js).
    const adminItems = (!IS_NATIVE && currentUser && isAdminEmail(currentUser.email))
        ? [{ key: 'admin', label: t.admin }]
        : [];

    const items = [...baseItems, ...resultItems, ...managerItems, ...adminItems];

    // In app-modus is er niets om uit te loggen — inloggen bestaat daar niet.
    const showLogout = !IS_NATIVE && !!currentUser;

    function handleNavigate(target) {
        setPage(target);
        setMenuOpen(false);
    }
    function handleLogoutClick() {
        setMenuOpen(false);
        if (typeof onLogout === 'function') onLogout();
    }

    const langSwitch = (
        <div className={styles.langSwitch} role="group" aria-label={t.languageLabel}>
            <button
                type="button"
                onClick={() => { setLang('nl'); setMenuOpen(false); }}
                className={`${styles.langBtn} ${lang === 'nl' ? styles.langBtnActive : ''}`}
                aria-label={t.switchToDutch}
                aria-pressed={lang === 'nl'}
            >
                NL
            </button>
            <button
                type="button"
                onClick={() => { setLang('en'); setMenuOpen(false); }}
                className={`${styles.langBtn} ${lang === 'en' ? styles.langBtnActive : ''}`}
                aria-label={t.switchToEnglish}
                aria-pressed={lang === 'en'}
            >
                EN
            </button>
        </div>
    );

    return (
        <div className={styles.bar}>
            <div className={styles.inner}>
                <div className={styles.row}>
                    {/* Het logo is ook de weg terug: naar je eigen overzicht
                        (in de app het startscherm), of naar je teamoverzicht
                        als je als manager bent ingelogd. Zonder label is dat
                        voor een schermlezer een plaatje in een knop. */}
                    <button
                        type="button"
                        onClick={() => {
                            tap();
                            handleNavigate(isManager ? 'team' : 'home');
                        }}
                        className={styles.logoBtn}
                        aria-label={isManager ? t.logoToTeam : t.logoToHome}
                        title={isManager ? t.logoToTeam : t.logoToHome}
                    >
                        <img src={tofLogo} alt="" className={styles.logoImg} />
                        <div className={styles.brand}>
                            <span className={styles.brandTitle}>
                                <span className={styles.brandWord}>TOF</span>
                                <span className={styles.brandAccent}>Persona</span>
                            </span>
                            <span className={styles.brandSub}>The Office Factory</span>
                        </div>
                    </button>

                    {/* App-modus: navigeren gebeurt in de balk onderin (zie
                        native/TabBar.jsx), binnen duimbereik. Bovenin blijft
                        alleen het merk staan.

                        Geen taalknop in de app. Je kiest je taal bij het
                        openen, die keuze wordt onthouden, en daarna hoort een
                        app niet halverwege van taal te wisselen — dat is een
                        instelling, geen navigatie. Op het web blijft de knop
                        wél staan: daar kom je binnen via een gedeelde link en
                        beslist die link de taal. */}
                    {IS_NATIVE ? null : !isMobile ? (
                        <div className={styles.navList}>
                            {items.map((item) => {
                                const active = page === item.key;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => handleNavigate(item.key)}
                                        className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                                    >
                                        {item.label}
                                    </button>
                                );
                            })}

                            {showLogout ? (
                                <button
                                    type="button"
                                    onClick={handleLogoutClick}
                                    className={styles.logoutBtn}
                                    title={currentUser.email || ''}
                                >
                                    <span className={styles.logoutDot} />
                                    {t.logout}
                                </button>
                            ) : null}

                            {langSwitch}
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            className={`${styles.menuBtn} ${menuOpen ? styles.menuBtnOpen : ''}`}
                        >
                            {menuOpen ? t.close : t.menu}
                        </button>
                    )}
                </div>

                {!IS_NATIVE && isMobile && menuOpen && (
                    <div className={styles.menuPanel}>
                        {items.map((item) => {
                            const active = page === item.key;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => handleNavigate(item.key)}
                                    className={`${styles.menuItem} ${active ? styles.menuItemActive : ''}`}
                                >
                                    {item.label}
                                </button>
                            );
                        })}

                        {showLogout ? (
                            <button
                                type="button"
                                onClick={handleLogoutClick}
                                className={styles.menuLogoutBtn}
                            >
                                {t.logoutWithEmail(currentUser.email)}
                            </button>
                        ) : null}

                        <div className={styles.menuLang}>{langSwitch}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
